import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  PTB_CARD_ANCHOR_Z_INDEX,
  computeCardAnchoredPosition,
  getCardElement,
} from './ptbCardAnchorUtils';

export const PTB_CARD_ANCHORED_ACK_VISIBLE_MS = 2700;
export const PTB_CARD_ANCHORED_ACK_FADE_MS = 300;
export const PTB_CARD_ANCHORED_ACK_TOTAL_MS =
  PTB_CARD_ANCHORED_ACK_VISIBLE_MS + PTB_CARD_ANCHORED_ACK_FADE_MS;

function PtbCardAnchoredAck({
  cardId = null,
  message = '',
  petName = '',
  phaseName = '',
  scrollContainerRef = null,
  onDismiss,
}) {
  const ackRef = useRef(null);
  const [isDismissing, setIsDismissing] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement: 'above',
  });

  const isPhaseMoveAck = Boolean(phaseName);
  const hasContent = isPhaseMoveAck || Boolean(message);

  const updatePosition = useCallback(() => {
    if (!cardId) {
      return;
    }

    const cardEl = getCardElement(cardId);
    const ackEl = ackRef.current;

    if (!cardEl || !ackEl) {
      return;
    }

    const nextPosition = computeCardAnchoredPosition(
      cardEl.getBoundingClientRect(),
      ackEl.getBoundingClientRect(),
      { horizontalAlign: 'left' },
    );

    setPosition({
      top: nextPosition.top,
      left: nextPosition.left,
      placement: nextPosition.placement,
    });
  }, [cardId]);

  useLayoutEffect(() => {
    if (!cardId || !hasContent) {
      return undefined;
    }

    updatePosition();

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        updatePosition();
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [cardId, hasContent, message, petName, phaseName, updatePosition]);

  useEffect(() => {
    if (!cardId || !hasContent) {
      return undefined;
    }

    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('resize', handleScrollOrResize);

    const scrollEl = scrollContainerRef?.current;
    scrollEl?.addEventListener('scroll', handleScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      scrollEl?.removeEventListener('scroll', handleScrollOrResize);
    };
  }, [cardId, hasContent, scrollContainerRef, updatePosition]);

  useEffect(() => {
    if (!cardId || !hasContent) {
      return undefined;
    }

    setIsDismissing(false);

    const fadeTimer = window.setTimeout(() => {
      setIsDismissing(true);
    }, PTB_CARD_ANCHORED_ACK_VISIBLE_MS);

    const dismissTimer = window.setTimeout(() => {
      onDismiss?.();
    }, PTB_CARD_ANCHORED_ACK_TOTAL_MS);

    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(dismissTimer);
    };
  }, [cardId, hasContent, message, petName, phaseName, onDismiss]);

  if (!cardId || !hasContent) {
    return null;
  }

  return createPortal(
    <div
      ref={ackRef}
      className={`ptb-card-anchored-ack ptb-card-anchored-ack--${position.placement}${
        isDismissing ? ' ptb-card-anchored-ack--dismissing' : ''
      }`}
      style={{
        top: position.top,
        left: position.left,
        zIndex: PTB_CARD_ANCHOR_Z_INDEX,
      }}
      role="status"
      aria-live="polite"
    >
      <span className="ptb-card-anchored-ack__check" aria-hidden="true">
        ✓
      </span>

      {isPhaseMoveAck ? (
        <span className="ptb-card-anchored-ack__message">
          <span
            className="ptb-card-anchored-ack__pet-name"
            title={petName}
          >
            {petName}
          </span>
          {' '}
          moved to {phaseName}
        </span>
      ) : (
        <span className="ptb-card-anchored-ack__message">{message}</span>
      )}
    </div>,
    document.body,
  );
}

export default PtbCardAnchoredAck;
